import './tradesView.scss'

import styled from '@emotion/styled'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { Space } from '../../../components/core-v2'
import { TradeCard } from '../../../components/core-v2/molecules/trade-card'
import { UserDisplayInfo } from '../../../types/user'
import { actions as builderActions } from '../../tradebuilder-v2/modules/00-builder/state/builder-slice'
import { useAppDispatch } from '../../tradebuilder-v2/store/hooks'
import { TradingAccount } from '../types'

export type TradesViewProps = {
  accounts: TradingAccount[]
  userInfo: UserDisplayInfo
}

const StyledList = styled.div`
  border-top: solid 1px var(--White90);
  height: calc(100vh - 413px);
  overflow-y: auto;
`

const TradesView = ({ accounts, userInfo: { userName, userRoles, userType } }: TradesViewProps) => {
  const [selectedAccountId, setSelectedAccountId] = useState<string>('')
  const location = useLocation()

  const onSelectAccount = useCallback(accountId => {
    setSelectedAccountId(accountId)
  }, [])

  // ABSA-10814: the streamlined "Amend fees" gesture navigates here with the amended account id in
  // router state so the user lands on its Set-economics fee grid. Select it ONCE, when it first
  // appears in the loaded queue - applying only once (via the ref) so that later card clicks are not
  // reverted back to the amended account. The account must be in scope for the current filter (Live
  // trades) for this to take effect.
  const requestedAccountId = (location.state as { selectedAccountId?: string } | null)
    ?.selectedAccountId
  const hasAppliedRequestedSelection = useRef(false)
  useEffect(() => {
    if (
      !hasAppliedRequestedSelection.current &&
      requestedAccountId &&
      accounts.some(account => account.accountId === requestedAccountId)
    ) {
      hasAppliedRequestedSelection.current = true
      setSelectedAccountId(requestedAccountId)
    }
  }, [requestedAccountId, accounts])

  const dispatch = useAppDispatch()
  useEffect(() => {
    if (selectedAccountId) {
      dispatch(
        builderActions.init({
          accountId: selectedAccountId,
          userName,
          userRoles,
          userType
        })
      )
    }
  }, [dispatch, userName, userRoles, userType, selectedAccountId])
  useEffect(() => {
    if (selectedAccountId) {
      const selectedAccountIsInScopeAccounts =
        accounts.find(item => item.accountId === selectedAccountId) !== undefined
      if (!selectedAccountIsInScopeAccounts) {
        dispatch(builderActions.resetSavedState())
        setSelectedAccountId('')
      }
    }
  }, [accounts, dispatch, selectedAccountId])

  return (
    <StyledList key="trades_view_list">
      <Space mt={3}>
        <ul key="trades_list_inner">
          {accounts.map(account => (
            <TradeCard
              key={account.accountId}
              isLiveTrade={account.newAccount === false}
              isSelected={account.accountId === selectedAccountId}
              trade={account}
              onClickAccount={() => onSelectAccount(account.accountId)}
            />
          ))}
        </ul>
      </Space>
    </StyledList>
  )
}

export default TradesView
